import { ref } from 'vue'
import { 
  collection, 
  doc, 
  addDoc, 
  getDoc, 
  getDocs, 
  updateDoc, 
  deleteDoc,
  query, 
  where, 
  orderBy, 
  limit,
  serverTimestamp 
} from 'firebase/firestore'
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage'
import { db, storage } from '@/services/firebase'
import imageCompression from 'browser-image-compression'

export function useJobs() {
  const loading = ref(false)
  const error = ref(null)

  // 이미지 압축 + WebP 변환
  const compressImage = async (file) => {
    try {
      // 1. 먼저 압축
      const options = {
        maxSizeMB: 0.5,           // 최대 500KB
        maxWidthOrHeight: 1280,   // 최대 1280px
        useWebWorker: true,
        fileType: 'image/webp'    // WebP로 변환
      }
      
      const compressedFile = await imageCompression(file, options)
      
      console.log('원본 크기:', (file.size / 1024).toFixed(2), 'KB')
      console.log('압축 후:', (compressedFile.size / 1024).toFixed(2), 'KB')
      
      return compressedFile
    } catch (e) {
      console.error('이미지 압축 실패:', e)
      return file // 실패 시 원본 반환
    }
  }

  // 이미지 업로드
  const uploadImage = async (file) => {
    // 이미지 압축 + WebP 변환
    const compressedFile = await compressImage(file)
    
    // 파일명에서 특수문자/한글 제거 + 확장자 webp로
    const timestamp = Date.now()
    const safeName = file.name.replace(/[^a-zA-Z0-9]/g, '_').replace(/\.[^.]+$/, '')
    const path = `jobs/${timestamp}_${safeName}.webp`
    
    const imageRef = storageRef(storage, path)
    const snapshot = await uploadBytes(imageRef, compressedFile)
    const downloadURL = await getDownloadURL(snapshot.ref)
    
    return downloadURL
  }

  // 공고 등록
  const createJob = async (jobData, userId) => {
    loading.value = true
    error.value = null
    try {
      const docRef = await addDoc(collection(db, 'jobs'), {
        ...jobData,
        userId,
        status: 'active',
        views: 0,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      })
      return docRef.id
    } catch (e) {
      error.value = '공고 등록에 실패했습니다.'
      throw e
    } finally {
      loading.value = false
    }
  }

  // 공고 상세 조회
  const getJob = async (jobId) => {
    loading.value = true
    error.value = null
    try {
      const docSnap = await getDoc(doc(db, 'jobs', jobId))
      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() }
      } else {
        error.value = '존재하지 않는 공고입니다.'
        return null
      }
    } catch (e) {
      error.value = '공고를 불러오는데 실패했습니다.'
      throw e
    } finally {
      loading.value = false
    }
  }

  // 공고 목록 조회
  const getJobs = async (filters = {}) => {
    loading.value = true
    error.value = null
    try {
      let q = collection(db, 'jobs')
      const constraints = []

      if (filters.isPremium !== undefined) {
        constraints.push(where('isPremium', '==', filters.isPremium))
      }
      if (filters.region) {
        constraints.push(where('region', '==', filters.region))
      }
      if (filters.industry) {
        constraints.push(where('industry', '==', filters.industry))
      }
      if (filters.status) {
        constraints.push(where('status', '==', filters.status))
      }

      constraints.push(orderBy('createdAt', 'desc'))
      
      if (filters.limit) {
        constraints.push(limit(filters.limit))
      }

      q = query(q, ...constraints)
      const querySnapshot = await getDocs(q)
      
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }))
    } catch (e) {
      error.value = '공고 목록을 불러오는데 실패했습니다.'
      throw e
    } finally {
      loading.value = false
    }
  }

  // 내 공고 목록
  const getMyJobs = async (userId) => {
    loading.value = true
    error.value = null
    try {
      const q = query(
        collection(db, 'jobs'),
        where('userId', '==', userId),
        orderBy('createdAt', 'desc')
      )
      const querySnapshot = await getDocs(q)
      
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }))
    } catch (e) {
      error.value = '공고 목록을 불러오는데 실패했습니다.'
      throw e
    } finally {
      loading.value = false
    }
  }

  // 공고 수정
  const updateJob = async (jobId, jobData) => {
    loading.value = true
    error.value = null
    try {
      await updateDoc(doc(db, 'jobs', jobId), {
        ...jobData,
        updatedAt: serverTimestamp()
      })
    } catch (e) {
      error.value = '공고 수정에 실패했습니다.'
      throw e
    } finally {
      loading.value = false
    }
  }

  // 공고 삭제 (연결된 배너들도 함께 삭제)
  const deleteJob = async (jobId) => {
    loading.value = true
    error.value = null
    try {
      // 1. 연결된 모든 배너 컬렉션에서 삭제
      const bannerCollections = ['banners', 'topBanners', 'subBanners', 'sidebarAds']
      
      for (const collectionName of bannerCollections) {
        const q = query(
          collection(db, collectionName),
          where('jobId', '==', jobId)
        )
        const snapshots = await getDocs(q)
        
        for (const bannerDoc of snapshots.docs) {
          await deleteDoc(bannerDoc.ref)
          console.log(`${collectionName}에서 배너 삭제:`, bannerDoc.id)
        }
      }

      // 2. 공고 삭제
      await deleteDoc(doc(db, 'jobs', jobId))
      console.log('공고 삭제 완료:', jobId)
      
    } catch (e) {
      error.value = '공고 삭제에 실패했습니다.'
      throw e
    } finally {
      loading.value = false
    }
  }

  // 조회수 증가
  const incrementViews = async (jobId, currentViews) => {
    try {
      await updateDoc(doc(db, 'jobs', jobId), {
        views: currentViews + 1
      })
    } catch (e) {
      console.error('조회수 증가 실패:', e)
    }
  }

  return {
    loading,
    error,
    compressImage,
    uploadImage,
    createJob,
    getJob,
    getJobs,
    getMyJobs,
    updateJob,
    deleteJob,
    incrementViews
  }
}

// 상수 데이터
export const JOB_CONSTANTS = {
  regions: [
    '서울', '경기', '인천', '부산', '대구', '광주', '대전', '울산', '세종',
    '강원', '충북', '충남', '전북', '전남', '경북', '경남', '제주'
  ],
  industries: [
    '아파트', '오피스텔', '도시형생활주택', '상가', '지식산업센터',
    '타운하우스', '단독주택', '레지던스', '호텔', '리조트',
    '토지', '재개발', '재건축', '분양권', '입주권', '기타'
  ],
  jobTypes: [
    '본부장', '팀장', '팀원', '인바운드', '아웃바운드', 'TM상담사',
    '거점알바', '전단알바', '홍보관도우미', '데스크', '경리',
    '기타'
  ],
  salaryTypes: [
    '월급', '주급', '일급', '시급', '건당', '계약수수료', '협의'
  ],
  genders: ['무관', '남성', '여성'],
  experiences: ['무관', '신입', '1년 이상', '3년 이상', '5년 이상']
}